import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCostTaskFavourites, setCostTaskFavourite, type CostTaskFavouriteInput, type CostTaskFavouriteRef } from "@workspace/api-client-react";
import type { CostLibraryTask } from "@shared/schema";
import { catalogueTaskKey } from "@/lib/quote-catalogue-item";

export function getAccountFavouritesKey(accountId: string | number) {
  return ["/api/cost-library/favourites", { account: accountId }] as const;
}

export function useCostTaskFavourites(accountId: string | number) {
  const client = useQueryClient();
  const queryKey = getAccountFavouritesKey(accountId);
  const query = useQuery({
    queryKey, queryFn: () => getCostTaskFavourites(), staleTime: 30_000,
  });
  const keys = useMemo(() => new Set((query.data ?? []).map(t => `${t.source}:${t.taskId}`)), [query.data]);
  const [error, setError] = useState<string | null>(null);
  const busy = useRef(false);
  const mutation = useMutation({
    mutationFn: (input: CostTaskFavouriteInput) => setCostTaskFavourite(input),
    onSuccess: async (_saved, input) => {
      // Patch only the initiating account; never copy task details into favourites.
      await client.cancelQueries({ queryKey });
      client.setQueryData<CostTaskFavouriteRef[]>(queryKey, old => {
        const next = (old ?? []).filter(t => t.source !== input.source || t.taskId !== input.taskId);
        return input.favourite ? [...next, { source: input.source, taskId: input.taskId }] : next;
      });
      void client.invalidateQueries({ queryKey });
    },
  });
  const toggle = async (task: CostLibraryTask) => {
    if (busy.current || !query.data || query.isError) return;
    busy.current = true;
    setError(null);
    try {
      await mutation.mutateAsync({
        source: task.source === "custom" ? "custom" : "default",
        taskId: task.id, favourite: !keys.has(catalogueTaskKey(task)),
      });
    } catch {
      setError("Could not save your favourite. Please try again.");
    } finally { busy.current = false; }
  };
  return {
    keys, toggle, pending: mutation.isPending,
    unavailable: query.isPending || query.isError,
    loading: query.isPending,
    error: query.isError ? "Could not load your favourites." : error,
    retry: () => { setError(null); void query.refetch(); },
  };
}
